'use client';

import { useEffect, useRef, useState } from 'react';

import { Check } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';

import { DemoLink } from '@/components/demo-link';
import { ScanEmailModal } from '@/components/modals/scan-email-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SCANNER_COPY } from '@/lib/content';
import { clearScanEmail, peekScanEmail } from '@/lib/scan-handoff';
import { scoreDomain, scoreTone, type ScanDimension } from '@/lib/scan-score';
import { cn } from '@/lib/utils';
import { EMAIL_REGEX, URL_REGEX } from '@/lib/validation';

type Phase = 'idle' | 'scanning' | 'result';

const STEP_MS = 850;

const DOT_POS = [
  { top: '14px', left: '58px', topSm: '10px', leftSm: '46px' },
  { top: '56px', left: '96px', topSm: '44px', leftSm: '78px' },
  { top: '92px', left: '40px', topSm: '74px', leftSm: '30px' },
  { top: '52px', left: '24px', topSm: '40px', leftSm: '16px' },
] as const;

function toneText(tone: ReturnType<typeof scoreTone>): string {
  if (tone === 'brand') return 'text-brand';
  if (tone === 'warning') return 'text-warning';
  return 'text-success';
}

function toneChip(tone: ReturnType<typeof scoreTone>): string {
  if (tone === 'brand') return 'text-brand bg-brand/16';
  if (tone === 'warning') return 'text-warning bg-warning/16';
  return 'text-success bg-success/16';
}

function dotColor(tone: ReturnType<typeof scoreTone>): string {
  if (tone === 'brand') return 'bg-brand';
  if (tone === 'warning') return 'bg-warning';
  return 'bg-success';
}

function Radar({
  dims,
  resolvedCount,
  size,
}: {
  dims: ScanDimension[];
  resolvedCount: number;
  size: 'lg' | 'sm';
}) {
  const box = size === 'lg' ? 'size-[120px]' : 'size-24';
  return (
    <div className={cn('relative shrink-0', box)} aria-hidden="true">
      <div className="absolute inset-0 rounded-full border" />
      <div
        className={cn(
          'absolute rounded-full border opacity-70',
          size === 'lg' ? 'inset-5' : 'inset-4',
        )}
      />
      {size === 'lg' && <div className="absolute inset-10 rounded-full border opacity-50" />}
      {size === 'lg' && (
        <div className="absolute inset-0 animate-sweep rounded-full bg-[conic-gradient(from_0deg,oklch(0.65_0.16_145/0.35),transparent_70deg)] motion-reduce:animate-none" />
      )}
      {dims.map((dim, index) => {
        const pos = DOT_POS[index];
        const resolved = index < resolvedCount;
        return (
          <span
            key={dim.name}
            className={cn(
              'absolute size-[9px] rounded-full',
              resolved ? dotColor(scoreTone(dim.score)) : 'bg-foreground/25',
              !resolved && 'animate-live-pulse motion-reduce:animate-none',
            )}
            style={{
              top: size === 'lg' ? pos.top : pos.topSm,
              left: size === 'lg' ? pos.left : pos.leftSm,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Picks up a handoff: the domain from ?domain= and the email from session storage. Reading
 * searchParams on the server would make /scan render on demand for every visitor, so both are read
 * on the client; needs a Suspense boundary around it.
 */
export function ReadinessScannerFromQuery({ className }: { className?: string }) {
  const domain = useSearchParams().get('domain');
  const [email] = useState(peekScanEmail);
  useEffect(() => clearScanEmail(), []);
  return (
    <ReadinessScanner
      className={className}
      initialDomain={domain ?? undefined}
      initialEmail={email}
    />
  );
}

/** A scan is only ever started with the email that paid for it, so the two travel together. */
type Scan = { scored: ReturnType<typeof scoreDomain>; email: string };

/** Auto-runs only when both halves of a handoff survived the trip; otherwise the visitor finishes
 *  the form themselves. */
function seedScan(domain: string | undefined, email: string | undefined): Scan | null {
  if (!domain || !URL_REGEX.test(domain.trim())) return null;
  if (!email || !EMAIL_REGEX.test(email.trim())) return null;
  return { scored: scoreDomain(domain), email: email.trim() };
}

export function ReadinessScanner({
  className,
  initialDomain,
  initialEmail,
}: {
  className?: string;
  initialDomain?: string;
  initialEmail?: string;
}) {
  const [input, setInput] = useState(initialDomain ?? '');
  const [error, setError] = useState(false);
  const [askingEmail, setAskingEmail] = useState(false);
  // A domain and email handed over from another page mean the visitor already asked for this scan.
  // Seeding here runs it on first paint; making them fill the form again reads as a dead end.
  const [scan, setScan] = useState<Scan | null>(() => seedScan(initialDomain, initialEmail));
  const [phase, setPhase] = useState<Phase>(scan ? 'scanning' : 'idle');
  const [progress, setProgress] = useState(0);
  const reportedScan = useRef<Scan | null>(null);
  // PostHog's React docs are explicit that the hook is the only safe accessor: importing the
  // singleton can read it before the provider initializes it.
  const posthog = usePostHog();

  function askForEmail() {
    if (!URL_REGEX.test(input.trim())) {
      setError(true);
      return;
    }
    setError(false);
    setAskingEmail(true);
  }

  function runScan(email: string) {
    setAskingEmail(false);
    setProgress(0);
    setPhase('scanning');
    setScan({ scored: scoreDomain(input), email });
  }

  // The staged reveal is driven from here rather than from runScan so that a scan and its teardown
  // share one lifecycle. Scheduled anywhere else, a remount (Strict Mode does this in dev) clears
  // timers that nothing will reschedule and the scan sits at "probing…" forever.
  useEffect(() => {
    if (!scan) return;
    const { scored } = scan;

    // The reveal is safe to replay on remount, the lead and its analytics are not, so those are
    // held to once per scan. scoreDomain is synchronous, so the report goes out with real scores
    // the moment the scan starts rather than waiting for the animation to finish.
    if (reportedScan.current !== scan) {
      reportedScan.current = scan;
      const scores = Object.fromEntries(scored.dims.map(dim => [dim.name, dim.score]));
      posthog?.capture('scan_started', { domain: scored.domain });
      void fetch('/api/scan-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: scan.email,
          domain: scored.domain,
          overall: scored.overall,
          scores,
          source: 'scanner',
          // Undefined until PostHog initializes (analytics is off outside production), and the
          // route falls back to the email in that case.
          distinctId: posthog?.get_distinct_id?.(),
        }),
        // Best-effort: a failed report shouldn't hold back the results the visitor came for.
      }).catch(() => {});
    }

    const ids = [1, 2, 3, 4].map(step =>
      window.setTimeout(() => setProgress(step), STEP_MS * step),
    );
    ids.push(
      window.setTimeout(() => {
        setPhase('result');
        posthog?.capture('scan_completed', {
          domain: scored.domain,
          overall: scored.overall,
          tier: scored.tier,
          scores: Object.fromEntries(scored.dims.map(dim => [dim.name, dim.score])),
        });
      }, STEP_MS * 5),
    );
    return () => ids.forEach(id => window.clearTimeout(id));
  }, [scan, posthog]);

  const result = scan?.scored ?? null;
  const dims = result?.dims ?? [];

  return (
    <div
      id="scanner"
      className={cn(
        'invert-surface mx-auto w-full max-w-[760px] overflow-hidden rounded-[14px] border shadow-lift',
        className,
      )}
    >
      {/* No heading of its own: this only ever renders on /scan, directly under a page title that
          asks the same question, and printing it twice read as a mistake. SCANNER_COPY.heading is
          still the phrasing llms.txt uses for the scan. */}
      <div className="flex flex-col gap-4.5 px-6 py-8 sm:px-9">
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <Input
            value={input}
            onChange={e => {
              setInput(e.target.value);
              setError(false);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') askForEmail();
            }}
            placeholder={SCANNER_COPY.placeholder}
            aria-label="Your website address"
            aria-invalid={error}
            className="h-14 min-h-14 flex-1 px-4 font-mono text-base sm:h-12 sm:min-h-12"
          />
          <Button
            type="button"
            className="h-14 px-6 text-base font-semibold sm:h-12"
            onClick={askForEmail}
          >
            {SCANNER_COPY.button}
          </Button>
        </div>
        {error && <p className="text-base text-brand">{SCANNER_COPY.validationError}</p>}
        <p className="text-base text-faint-foreground">
          {SCANNER_COPY.microcopyBefore}
          <DemoLink className="text-muted-foreground underline underline-offset-3">
            {SCANNER_COPY.microcopyLink}
          </DemoLink>
          {SCANNER_COPY.microcopyAfter}
        </p>
      </div>

      {phase === 'scanning' && result && (
        <div
          aria-live="polite"
          className="flex flex-col items-center gap-7 border-t bg-muted px-6 py-6 sm:flex-row sm:px-9"
        >
          <Radar dims={dims} resolvedCount={progress} size="lg" />
          <div className="flex w-full flex-1 flex-col gap-2.5 font-mono text-sm">
            {dims.map((dim, index) => {
              const resolved = index < progress;
              const probing = index === progress;
              return (
                <div
                  key={dim.name}
                  className={cn(
                    'flex justify-between gap-4',
                    resolved || probing ? 'opacity-100' : 'opacity-50',
                  )}
                >
                  <span className="tracking-widest">{dim.name.toUpperCase()}</span>
                  <span
                    className={resolved ? toneText(scoreTone(dim.score)) : 'text-faint-foreground'}
                  >
                    {resolved ? dim.legend : probing ? 'probing…' : 'queued'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {phase === 'result' && result && (
        <div aria-live="polite" className="border-t">
          <div className="flex items-center justify-between gap-4 border-b bg-muted px-6 py-4 sm:px-9">
            <span className="font-mono text-sm tracking-label">
              {SCANNER_COPY.resultKicker} · {result.domain}
            </span>
            <span className="rounded-full border px-2.5 py-0.5 font-mono text-xs tracking-label text-faint-foreground">
              {SCANNER_COPY.simulatedLabel}
            </span>
          </div>
          <div className="flex flex-col items-center gap-7 px-6 py-7 sm:flex-row sm:px-9">
            <Radar dims={dims} resolvedCount={dims.length} size="sm" />
            <p className="flex-1 text-lg leading-snug font-semibold">{result.verdict}</p>
          </div>
          <div className="px-6 pb-6 sm:px-9">
            {dims.map(dim => (
              <div
                key={dim.name}
                className="flex items-center justify-between gap-4 border-t py-2.5 text-base"
              >
                <span className="font-semibold">{dim.name}</span>
                <span
                  className={cn(
                    'rounded-full px-2.5 py-0.5 font-mono text-xs tracking-widest',
                    toneChip(scoreTone(dim.score)),
                  )}
                >
                  {dim.tag}
                </span>
              </div>
            ))}
          </div>
          <p className="border-t px-6 py-4 text-base text-muted-foreground sm:px-9">
            {SCANNER_COPY.resultFooter}
          </p>
          <div className="flex flex-col items-start gap-3 border-t bg-muted px-6 py-7 sm:px-9">
            <p className="flex items-start gap-1.5 text-base">
              <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-success" />
              <span>
                {SCANNER_COPY.emailSuccessPrefix}
                <span className="font-mono text-base">{scan?.email}</span>.
              </span>
            </p>
            <Button asChild>
              <DemoLink>{SCANNER_COPY.emailDemoCta}</DemoLink>
            </Button>
          </div>
        </div>
      )}

      <ScanEmailModal
        open={askingEmail}
        domain={input.trim()}
        onClose={() => setAskingEmail(false)}
        onConfirm={runScan}
      />
    </div>
  );
}
